//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { IReduxState } from "@reducers/root.reducer";
import { createFragmentContainer } from "react-relay";
import withStyles from "@material-ui/core/styles/withStyles";
// AutopilotForm imports
import { IProps, IContainer, IState } from "./AutopilotForm.d";
import { styles } from "./AutopilotForm.styles";
import { autopilotFormFragments } from "./AutopilotForm.gql";
// other components
import { OrganizerAutopilotPausedFreemiumCallout } from "./organizer-autopilot-paused-freemium-callout";
import { AutopilotFlexibility } from "@components/autopilot-flexibility";
// mutation
// util
import { isApPausedFromFeatureGrid, isMoveRangeEnabledFromFeatureGrid } from "@util/event.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { setPricingDialogOpen } from "@actions/payments.actions";
import { track, TrackingEvents } from "@util/analytics.util";
import { EFlex } from "@util/event-tag.util";
import { DayOffsetTimeSlot } from "@clockwise/client-commons/src/util/time-slot";
// material-ui
// import { Typography } from "@material-ui/core";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { getValue } from "@src/util/errorable.util";
import { AutopilotEventStatusEnum } from "@clockwise/schema";

//////////////////
// COMPONENT
//////////////////
export class AutopilotFormCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      sendingInvite: false,
      sentInvite: false,
    };
  }

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getDefaultFlex = () => {
    const { event } = this.props;

    return (
      this.props.defaultFlex ??
      ((event &&
        event.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        ((event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
          .movementType as unknown) as EFlex)) ||
        EFlex.Day)
    );
  };

  private isToggledOn = () => {
    const { flex } = this.props.formState;
    return flex === EFlex.Day || flex === EFlex.Week;
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onToggle = () => {
    // important!!
    // if currently on, toggling turns autopilot off, which pins it
    const flex = this.isToggledOn() ? EFlex.Pin : this.getDefaultFlex();
    this.props.onChange({
      ...this.props.formState,
      flex,
    });
  };

  private onChangeFlex = (flex: EFlex) => {
    this.props.onChange({
      ...this.props.formState,
      flex,
    });
  };

  private onChangeRoom = () => {
    this.props.onChange({
      ...this.props.formState,
      allowSimilarRooms: !this.props.formState.allowSimilarRooms,
    });
  };

  private onChangeTime = (
    allowedStart: DayOffsetTimeSlot | null,
    allowedEnd: DayOffsetTimeSlot | null,
  ) => {
    this.props.onChange({
      ...this.props.formState,
      timeRange: { allowedStart, allowedEnd },
    });
  };

  private onClickView = () => {
    this.props.setPricingDialogOpen(true);
    track(TrackingEvents.PAYWALLS.MOVE_RANGES_CLICK_VIEW_PLANS);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderAutopilotStatusCallout = () => {
    const { org, event } = this.props;

    if (!event) {
      return "";
    }

    if (isApPausedFromFeatureGrid(event) && this.isToggledOn()) {
      return (
        <div className="mb-3">
          <OrganizerAutopilotPausedFreemiumCallout event={event} org={org} />
        </div>
      );
    }

    return "";
  };

  public render() {
    const { showRoom, formState, isDisabled, event } = this.props;

    // do not show warning if event is toggled on
    const warning =
      this.props.warning !== undefined && !this.isToggledOn() ? this.props.warning : undefined;
    const currentFlex = formState.flex !== EFlex.Pin ? formState.flex : this.getDefaultFlex();

    const intersectionOfAttendeesMeetingHours =
      getValue(event?.suggestedAutopilotSettingsResponseV3)?.intersectionOfAttendeesMeetingHours ||
      null;

    // we render a fixed autopilotFlexibility for events
    // that have an AP status of Paused or have AP paused from the feature grid
    // or that cannot move because attendee meeting hours don't intersect
    const apStatus = getValue(event?.autopilotEventStatus)?.status;

    const eventFixed = event
      ? (isApPausedFromFeatureGrid(event) ||
          apStatus === AutopilotEventStatusEnum.Paused ||
          !intersectionOfAttendeesMeetingHours) &&
        this.isToggledOn()
      : false;

    return (
      <>
        {this.maybeRenderAutopilotStatusCallout()}
        <AutopilotFlexibility
          event={this.props.event ?? null}
          org={this.props.org ?? null}
          selectedDate={this.props.selectedDate}
          onChangeFlex={this.onChangeFlex}
          onChangeRoom={this.onChangeRoom}
          onChangeTime={this.onChangeTime}
          selectedFlex={currentFlex}
          allowSimilarRooms={!!formState.allowSimilarRooms}
          disabled={isDisabled}
          showRoom={showRoom}
          onClickView={this.onClickView}
          allowedStart={formState.timeRange?.allowedStart}
          allowedEnd={formState.timeRange?.allowedEnd}
          planRestricted={event ? !isMoveRangeEnabledFromFeatureGrid(event) : false}
          isExtension={false}
          onToggle={this.onToggle}
          warning={warning}
          fixed={eventFixed}
          attendeeMeetingHoursIntersect={!!intersectionOfAttendeesMeetingHours}
        />
      </>
    );
  }
}

/////////////////
// With Styles //
/////////////////

export const AutopilotFormCmptStyled = withStyles(styles)(AutopilotFormCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const AutopilotFormRedux = connect(mapStateToProps, {
  snackBarOpen,
  setPricingDialogOpen,
})(AutopilotFormCmptStyled);

//////////////////
// RELAY
//////////////////
export const AutopilotForm = createFragmentContainer(AutopilotFormRedux, autopilotFormFragments);
